import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { Mail, UserCheck } from 'lucide-react';
import { api } from '../lib/api';
import PageHero from '../components/PageHero';
import PageBlocks, { usePageBlocks } from '../components/PageBlocks';
import { BlocksRenderer } from '../components/BlockRenderer';
import ContactForm from '../components/ContactForm';
import { useTranslation } from '../i18nContext';
import { isValidLocale } from '../i18n';

export default function ContactPage() {
  const t = useTranslation();
  const { lang } = useParams();
  const locale = lang && isValidLocale(lang) ? lang : 'es';
  const { blocks, replacesDefault } = usePageBlocks('contact');
  const [params] = useSearchParams();
  const requestedInterpreter = params.get('interpreter') || '';
  const requestedName = params.get('name') || '';
  const [pageCfg, setPageCfg] = useState({});

  useEffect(() => {
    let alive = true;
    api.get('/public-config')
      .then(({ data }) => {
        if (alive && data?.contact_page) setPageCfg(data.contact_page);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (replacesDefault) {
    return <BlocksRenderer blocks={blocks} locale={locale} />;
  }

  // Admin-editable text overrides (WYSIWYG editor) with i18n fallback.
  const isEn = t.locale === 'en';
  const ov = (k) => (pageCfg && pageCfg[isEn ? `${k}_en` : `${k}_es`]) || '';
  const heroLabel = ov('label') || t.contact.label;
  const heroTitle = ov('title') || t.contact.title;
  const heroSubtitle = ov('subtitle') || t.contact.subtitle;
  const infoTitle = ov('info_title') || t.contact.infoTitle;
  const emailLabel = ov('email_label') || t.contact.emailLabel;
  const contactEmail = (pageCfg && pageCfg.contact_email) || 'cmic@cmicmexico.org';
  const infoNote = ov('info_note') || t.contact.infoNote;

  return (
    <>
      <PageHero label={heroLabel} title={heroTitle} subtitle={heroSubtitle} />
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {requestedInterpreter && requestedName && (
            <div className="mb-8 max-w-4xl mx-auto bg-gradient-to-r from-cmic-50 to-amber-50 border border-cmic-200 rounded-2xl p-5 flex items-start gap-4" data-testid="contact-interpreter-banner">
              <div className="w-10 h-10 rounded-xl bg-cmic-100 flex items-center justify-center flex-shrink-0">
                <UserCheck size={20} className="text-cmic-600" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-cmic-700 font-bold mb-1">
                  {t.locale === 'es' ? 'Solicitud directa' : 'Direct request'}
                </p>
                <p className="text-charcoal-700 text-sm">
                  {t.locale === 'es'
                    ? <>Estás solicitando los servicios de <strong className="text-cmic-700">{requestedName}</strong>. Tu consulta será dirigida específicamente a este intérprete.</>
                    : <>You're requesting services from <strong className="text-cmic-700">{requestedName}</strong>. Your inquiry will be forwarded to this interpreter.</>}
                </p>
              </div>
            </div>
          )}
          <div className="grid lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-xl font-semibold text-charcoal-800 mb-4">{infoTitle}</h2>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-cmic-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail size={20} className="text-cmic-500" />
                  </div>
                  <div>
                    <p className="text-sm text-charcoal-400 mb-1">{emailLabel}</p>
                    <a href={`mailto:${contactEmail}`} className="text-cmic-600 hover:text-cmic-700 font-medium hover:underline">
                      {contactEmail}
                    </a>
                  </div>
                </div>
              </div>
              <div className="bg-cmic-50 rounded-xl p-6">
                <p className="text-sm text-charcoal-600 leading-relaxed">{infoNote}</p>
              </div>
            </div>

            <div className="lg:col-span-3">
              <ContactForm requestedInterpreter={requestedInterpreter} requestedName={requestedName} />
            </div>
          </div>
        </div>
      </section>
      <PageBlocks pageKey="contact" />
    </>
  );
}
